import apiClientService from "./ApiClientService";

const RATINGS_BASE = "/instructor-ratings";

async function readResponse(res, fallback = null) {
   const text = await res.text().catch(() => "");
   if (!text) return fallback;
   try {
      return JSON.parse(text);
   } catch {
      return text;
   }
}

async function throwApiError(res, fallbackMessage) {
   const payload = await readResponse(res, "");
   const message =
      typeof payload === "string"
         ? payload
         : payload?.message || payload?.error || fallbackMessage;
   throw new Error(message || fallbackMessage);
}

function appendQuery(params = {}) {
   const qs = new URLSearchParams();
   Object.entries(params || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      qs.set(key, String(value));
   });
   const query = qs.toString();
   return query ? `?${query}` : "";
}

export async function getPendingInstructorRatings() {
   const res = await apiClientService.get(`${RATINGS_BASE}/pending`);
   if (!res.ok) {
      await throwApiError(
         res,
         "Nu am putut incarca lectiile fara rating.",
      );
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

export async function submitInstructorRating(payload = {}) {
   const body = {
      reservationId: payload.reservationId,
      rating: payload.rating,
   };

   const comment = String(payload.comment || "").trim();
   if (comment) body.comment = comment;

   const res = await apiClientService.post(RATINGS_BASE, body);
   if (!res.ok) {
      await throwApiError(res, "Nu am putut trimite ratingul.");
   }
   return readResponse(res, null);
}

export async function getMyInstructorRatings() {
   const res = await apiClientService.get(`${RATINGS_BASE}/my`);
   if (!res.ok) {
      await throwApiError(res, "Nu am putut incarca ratingurile tale.");
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

export async function getAdminRatingsOverview(filters = {}) {
   const res = await apiClientService.get(
      `${RATINGS_BASE}/admin/overview${appendQuery(filters)}`,
   );
   if (!res.ok) {
      await throwApiError(res, "Nu am putut incarca statisticile de rating.");
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

export async function getAdminRatingsMonthly(filters = {}) {
   const res = await apiClientService.get(
      `${RATINGS_BASE}/admin/monthly${appendQuery(filters)}`,
   );
   if (!res.ok) {
      await throwApiError(
         res,
         "Nu am putut incarca statisticile lunare de rating.",
      );
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

/**
 * GET /instructor-ratings/admin/count?since=<ms> — câte recenzii au venit după `since` (insigna „Recenzii”),
 * fără lista întreagă. null — serverul nu are încă ruta (404): apelantul folosește lista.
 */
export async function getAdminRatingsNewCount(since) {
   const res = await apiClientService.get(
      `${RATINGS_BASE}/admin/count${appendQuery({ since: since > 0 ? since : undefined })}`,
   );
   if (res.status === 404) return null;
   if (!res.ok) {
      await throwApiError(res, "Nu am putut incarca numarul recenziilor.");
   }
   const data = await readResponse(res, null);
   const count = Number(data?.count);
   return Number.isFinite(count) ? count : null;
}

export async function getAdminRatingsAll(filters = {}) {
   const res = await apiClientService.get(
      `${RATINGS_BASE}/admin/all${appendQuery(filters)}`,
   );
   if (!res.ok) {
      await throwApiError(res, "Nu am putut incarca ratingurile.");
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

export async function getAdminRatingsAnnual(filters = {}) {
   const res = await apiClientService.get(
      `${RATINGS_BASE}/admin/annual${appendQuery(filters)}`,
   );
   if (!res.ok) {
      await throwApiError(
         res,
         "Nu am putut incarca statisticile anuale de rating.",
      );
   }
   const data = await readResponse(res, []);
   return Array.isArray(data) ? data : [];
}

export async function deleteAdminRating(ratingId) {
   const id = Number(ratingId);
   if (!Number.isFinite(id) || id <= 0) {
      throw new Error("ID recenzie invalid.");
   }

   const res = await apiClientService.delete(`${RATINGS_BASE}/admin/${id}`);
   if (!res.ok) {
      await throwApiError(res, "Nu am putut sterge recenzia.");
   }
   return readResponse(res, { ok: true });
}

export async function updateAdminRating(ratingId, payload = {}) {
   const id = Number(ratingId);
   if (!Number.isFinite(id) || id <= 0) {
      throw new Error("ID recenzie invalid.");
   }

   const body = {
      rating: Number(payload.rating),
   };

   if (payload.comment !== undefined) {
      body.comment = String(payload.comment || "").trim();
   }

   const res = await apiClientService.patch(
      `${RATINGS_BASE}/admin/${id}`,
      body,
   );
   if (!res.ok) {
      await throwApiError(res, "Nu am putut actualiza recenzia.");
   }
   return readResponse(res, null);
}
